import type React from "react";
import { useEffect, useState } from "react";
import ApiService from "../services/api";
import type { ReferralSource } from "../types";

interface CommunicationsSectionProps {
	subscribeToUpdates: boolean;
	howDidYouHearAboutUs: string;
	onSubscribeToUpdatesChange: (value: boolean) => void;
	onHowDidYouHearAboutUsChange: (value: string) => void;
	// Validation props
	getFieldError?: (fieldName: string) => string | null;
	hasFieldError?: (fieldName: string) => boolean;
	touchField?: (fieldName: string) => void;
	validateSingleField?: (
		fieldName: string,
		value: string | number | boolean,
	) => void;
}

export const CommunicationsSection: React.FC<CommunicationsSectionProps> = ({
	subscribeToUpdates,
	howDidYouHearAboutUs,
	onSubscribeToUpdatesChange,
	onHowDidYouHearAboutUsChange,
	getFieldError,
	hasFieldError,
	touchField,
	validateSingleField,
}) => {
	const [referralSources, setReferralSources] = useState<ReferralSource[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// Fetch referral sources from backend using API service
		const fetchReferralSources = async () => {
			try {
				setLoading(true);
				setError(null);
				const data = await ApiService.getReferralSources();
				setReferralSources(data);
			} catch (err) {
				console.error("Failed to fetch referral sources:", err);
				setError(
					err instanceof Error
						? err.message
						: "Failed to load referral sources",
				);
				setReferralSources([]);
			} finally {
				setLoading(false);
			}
		};

		fetchReferralSources();
	}, []);

	return (
		<section id="communications-section">
			<div className="flex flex-col">
				<h2 className="inline-block text-lg mt-8">
					How did you hear about us?
				</h2>
				<select
					name="communications-section--referral-sources"
					id="communications-section--referral-sources"
					className={`gw-select text-md flex flex-grow mb-4 rounded-full border h-12 border-none p-2 px-4 focus-outline ${
						hasFieldError?.("howDidYouHearAboutUs") ? "border-red-500" : ""
					}`}
					value={howDidYouHearAboutUs}
					onChange={(e) => {
						onHowDidYouHearAboutUsChange(e.target.value);
						validateSingleField?.("howDidYouHearAboutUs", e.target.value);
					}}
					onBlur={() => {
						touchField?.("howDidYouHearAboutUs");
						validateSingleField?.("howDidYouHearAboutUs", howDidYouHearAboutUs);
					}}
					disabled={loading || error !== null}
				>
					{loading && (
						<option value="" disabled>
							Loading options...
						</option>
					)}
					{error && (
						<option value="" disabled>
							Error loading options
						</option>
					)}
					{!loading && !error && (
						<>
							<option value="" disabled>
								Please select one...
							</option>
							{referralSources.map((source) => (
								<option key={source.value} value={source.value}>
									{source.label}
								</option>
							))}
						</>
					)}
				</select>
				{error && (
					<p className="text-sm" style={{ color: "red" }}>
						{error}
					</p>
				)}
				{getFieldError?.("howDidYouHearAboutUs") && (
					<p
						className="text-sm"
						style={{ color: "red", marginTop: "-0.75rem" }}
					>
						{getFieldError("howDidYouHearAboutUs")}
					</p>
				)}
			</div>

			<div className="flex flex-col items-start">
				<label className="gw-checkbox mb-4">
					<input
						type="checkbox"
						id="communications-section--subscribe-to-updates"
						name="communications-section--subscribe-to-updates"
						checked={subscribeToUpdates}
						onChange={(e) => onSubscribeToUpdatesChange(e.target.checked)}
					/>
					Send me news and updates
					<div className="checkmark rounded-sm"></div>
				</label>
			</div>
		</section>
	);
};
